import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { debounceTime, map, shareReplay } from 'rxjs/operators';
import { Router } from '@angular/router';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { BaseViewModel } from '../../../../../../models/base/base-view-model';
import { ToastService } from '../../../../../../services/toast-service';
import { InternalUsersDomainModel } from '../../../../../../domainModels/internal-users-domain-model.service';
import { PermissionService } from '../../../../../../services/permission-service';
import { UserDomainModel } from '../../../../../../domainModels/user-domain-model';
import { PermissionDomainModel } from '../../../../../../domainModels/permission-domain-model';
import { SlidingWindowOptions } from '../../../../../../models/shared/sliding-window-options';
import { InternalUserTableFilterRequest } from '../../../../../../models/shared/odata-filter-requests/internal-user-table-filter-request';
import { InternalUserTableOrderbyRequest } from '../../../../../../models/shared/odata-sorting-requests/internal-user-table-orderby-request';
import { DropDownItem } from '../../../../../../models/shared/stylesheet/drop-down-item';
import { InternalUser } from '../../../../../../models/users/dto/internal-user';
import { OdataFilterGenerator } from '../../../../../../interfaces/odata-filter-generator';
import { ODataQueryOptions } from '../../../../../../models/shared/odata-query-options';
import { CustomError } from '../../../../../../models/shared/custom-error';
import { ScreenService } from '../../../../../../services/screen-service.service';
import { FormControl, FormGroup } from '@angular/forms';
import { isoDateStringValidator } from '../../../../../../validators/iso-date-string-validator';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Injectable()
export class AllInternalUsersDataTableViewModel extends BaseViewModel {
  constructor(private toastService: ToastService, private router: Router) {
    super();
  }

  private domainModel = inject(InternalUsersDomainModel);
  private permissionService = inject(PermissionService);
  private userDomainModel = inject(UserDomainModel);
  private permissionDomainModel = inject(PermissionDomainModel);
  private screenService = inject(ScreenService);

  public internalUsers$ = this.domainModel.allInternalUsers$.pipe(map(users => users ?? []));
  public internalUserTotalCount$ = this.domainModel.allInternalUsersTotalCount$;

  private _showFilterWindow = new BehaviorSubject<boolean>(false);
  public readonly showFilterWindow$ = this._showFilterWindow as Observable<boolean>;

  private _activeFilterCount = new BehaviorSubject<number>(0);
  public readonly activeFilterCount$ = this._activeFilterCount as Observable<number>;

  private _activeSearchCriteria = new BehaviorSubject<number>(0);
  public readonly activeSearchCriteria$ = this._activeSearchCriteria as Observable<number>;

  public filterWindowOptions$ = of(
    new SlidingWindowOptions($localize`Filter Users`, $localize`Reset All`, $localize`Apply Filters`)
  );

  public readonly roleOptions$ = this.permissionDomainModel.roles$;

  public readonly statusOptions = [
    { label: $localize`Active`, value: true },
    { label: $localize`Inactive`, value: false }
  ];

  private _loadingInternalUsers = new BehaviorSubject<boolean>(false);
  public loadingInternalUsers$ = this._loadingInternalUsers as Observable<boolean>;

  public canViewInternalUserDetails$ = this.permissionService.permissionGranted$([1020]);

  private _internalUserFilterRequest = new BehaviorSubject<InternalUserTableFilterRequest>(
    new InternalUserTableFilterRequest()
  );
  public readonly internalUserFilterRequest$ = this._internalUserFilterRequest
    .distinctUniquelyIdentifiable()
    .pipe(shareReplay({ bufferSize: 1, refCount: true }));

  private _internalUserOrderbyRequest = new BehaviorSubject<InternalUserTableOrderbyRequest>(
    new InternalUserTableOrderbyRequest()
  );
  public readonly internalUserOrderbyRequest$ = this
    ._internalUserOrderbyRequest as Observable<InternalUserTableOrderbyRequest>;
  public readonly activeSort$ = this.internalUserOrderbyRequest$.pipe(
    map(orderByRequest => orderByRequest.getOrderByString() ?? '')
  );

  private _currPageNumber = new BehaviorSubject<number>(0);
  public readonly currPageNumber$ = this._currPageNumber as Observable<number>;

  private _pageSize = new BehaviorSubject<number>(10);
  public readonly pageSize$ = this._pageSize as Observable<number>;

  private _searchCriteriaChangedSubject = new Subject<InternalUserTableFilterRequest>();
  public readonly searchCriteriaChanged$ = this
    ._searchCriteriaChangedSubject as Observable<InternalUserTableFilterRequest>;

  private _selectedDates = new BehaviorSubject<NgbDate[]>([]);
  public readonly selectedDates$ = this._selectedDates as Observable<NgbDate[]>;

  public clearSearches$ = new Subject<void>();

  public statusDropdowns$ = of([
    new DropDownItem($localize`Active`, 'true', false),
    new DropDownItem($localize`Inactive`, 'false', false)
  ]);

  public readonly searchFormGroup = new FormGroup({
    firstName: new FormControl<string | null>(null),
    lastName: new FormControl<string | null>(null),
    email: new FormControl<string | null>(null),
    roleName: new FormControl<string | null>(null),
    status: new FormControl<string | null>(null),
    lastLoginDate: new FormControl<string | null>(null, {
      validators: [isoDateStringValidator]
    })
  });

  public readonly filterFormGroup = new FormGroup({
    startDate: new FormControl<string | null>(null, { validators: [isoDateStringValidator] }),
    active: new FormControl<boolean[]>([], { nonNullable: true }),
    roles: new FormControl<string[]>([], { nonNullable: true })
  });

  private searchCriteriaChangedSubscription = this.searchCriteriaChanged$
    .distinctUniquelyIdentifiable()
    .pipe(debounceTime(500))
    .subscribeWhileAlive({
      owner: this,
      next: req => {
        this._internalUserFilterRequest.next(req);
        this._activeSearchCriteria.next(req.getSearchCount());
      }
    });

  private listenToSearchFormGroup = this.searchFormGroup.valueChanges
    .pipe(debounceTime(200), takeUntilDestroyed())
    .subscribe({
      next: value => {
        const lastLoginControl = this.searchFormGroup.controls.lastLoginDate;
        if (lastLoginControl.value && lastLoginControl.invalid) return;

        this.internalUserFilterRequest$.once(req => {
          const copy = Object.assign(new InternalUserTableFilterRequest(), req);
          copy.searchFirstName = value.firstName ?? '';
          copy.searchLastName = value.lastName ?? '';
          copy.searchEmail = value.email ?? '';
          copy.searchRoleName = value.roleName ?? '';
          copy.searchStatus = value.status ?? '';
          copy.searchLastLogin = value.lastLoginDate ?? '';
          this._internalUserFilterRequest.next(copy);
          this._activeSearchCriteria.next(copy.getSearchCount());
        });
      }
    });

  private listenToFetchInternalUsers = combineLatest([
    this.internalUserFilterRequest$,
    this.internalUserOrderbyRequest$,
    this.currPageNumber$,
    this.pageSize$
  ])
    .pipe(debounceTime(100))
    .subscribe(([filterRequest, orderbyRequest, currPage, pageSize]) => {
      this.fetchInternalUsers(filterRequest, orderbyRequest, currPage, pageSize);
    });

  public toggleFilterWindow(): void {
    this._showFilterWindow.once(show => {
      this._showFilterWindow.next(!show);
      show ? this.screenService.hideOverlay() : this.screenService.showOverlay();
    });
  }

  public internalUserClicked(u: InternalUser) {
    this.userDomainModel.userId$.once(uId => {
      if (uId === u?.id) {
        this.router.navigate(['/profile/view']).then();
      } else {
        this.router.navigate([`settings/internal-users/${u.id}`]).then();
      }
    });
  }

  public setPageSize(size: number): void {
    this._pageSize.next(size);
  }

  public setSkip(skip: number): void {
    this.pageSize$.once(pageSize => {
      this._currPageNumber.next(Math.floor(skip / (pageSize || 1)));
    });
  }

  public submitFilterForm(): void {
    if (this.filterFormGroup.invalid) return;
    const req = this.buildFilterRequestFromForm();
    const copy = Object.assign(new InternalUserTableFilterRequest(), req);
    this._activeFilterCount.next(copy.getFilterCount());
    this._internalUserFilterRequest.next(copy);
    this.toggleFilterWindow();
  }

  public searchCriteriaChanged(req: OdataFilterGenerator): void {
    const copy = Object.assign(new InternalUserTableFilterRequest(), req);
    this._searchCriteriaChangedSubject.next(copy as InternalUserTableFilterRequest);
  }

  public resetFilterForm(): void {
    this.clearFilterForm();
  }

  public clearFilterForm(): void {
    this.filterFormGroup.reset();
    this.internalUserFilterRequest$.once(req => {
      const copy = Object.assign(new InternalUserTableFilterRequest(), req);
      copy.clearFilters();
      this._internalUserFilterRequest.next(copy as InternalUserTableFilterRequest);
      this._activeFilterCount.next(0);
      this._selectedDates.next([]);
    });
  }

  public resetFilterField(fieldKeys: string[]): void {
    fieldKeys.forEach(fieldKey => {
      const control = this.filterFormGroup.get(fieldKey);
      if (control) {
        const defaultValue = Array.isArray(control.value) ? [] : null;
        control.reset(defaultValue);
      }
    });
  }

  public updateCurrPageNumber(currPage: number): void {
    this._currPageNumber.next(currPage);
  }

  public handleSortReq(sortReq: string): void {
    this._internalUserOrderbyRequest.next(new InternalUserTableOrderbyRequest(sortReq));
  }

  public clearAllFilters(): void {
    this._internalUserFilterRequest.next(new InternalUserTableFilterRequest());
    this._activeSearchCriteria.next(0);
    this._activeFilterCount.next(0);
    this._selectedDates.next([]);
    this.searchFormGroup.reset();
    this.filterFormGroup.reset();
  }

  public clearSearches(): void {
    this.searchFormGroup.reset();
    this.clearSearches$.next();
  }

  public setDateFilters(dates: NgbDate[]): void {
    this._selectedDates.next(dates);
  }

  public toggleRole(roleId: string): void {
    const current = this.filterFormGroup.controls.roles.value;
    if (current.includes(roleId)) {
      this.filterFormGroup.controls.roles.setValue(current.filter(value => value !== roleId));
    } else {
      this.filterFormGroup.controls.roles.setValue([...current, roleId]);
    }
  }

  public toggleActiveStatus(active: boolean): void {
    const current = this.filterFormGroup.controls.active.value;
    if (current.includes(active)) {
      this.filterFormGroup.controls.active.setValue(current.filter(value => value !== active));
    } else {
      this.filterFormGroup.controls.active.setValue([...current, active]);
    }
  }

  private buildFilterRequestFromForm(): InternalUserTableFilterRequest {
    const form = this.filterFormGroup.getRawValue();
    const searchForm = this.searchFormGroup.getRawValue();
    const request = new InternalUserTableFilterRequest();
    request.searchFirstName = searchForm.firstName ?? '';
    request.searchLastName = searchForm.lastName ?? '';
    request.searchEmail = searchForm.email ?? '';
    request.searchRoleName = searchForm.roleName ?? '';
    request.searchStatus = searchForm.status ?? '';
    request.searchLastLogin = searchForm.lastLoginDate ?? '';
    request.active = form.active ?? [];
    request.roles = form.roles ?? [];
    request.startDate = form.startDate ?? '';
    request.endDate = '';
    return request;
  }

  private fetchInternalUsers(
    filterReq: InternalUserTableFilterRequest,
    orderbyReq: InternalUserTableOrderbyRequest,
    currPage: number,
    pageSize: number,
    showSpinner = true
  ): void {
    const lm = $localize`Loading Internal Users`;
    if (showSpinner) {
      this._loadingOpts.addRequest(lm);
    }
    this._loadingInternalUsers.next(true);
    const oDataParams = new ODataQueryOptions();
    const filterString = filterReq.getFilterString();
    const orderbyString = orderbyReq.getOrderByString();
    if (!!filterString) {
      oDataParams.setFilter(filterString);
    }
    if (!!orderbyString) {
      oDataParams.setOrderBy(orderbyString);
    }
    oDataParams.setCount(true);
    oDataParams.setTop(pageSize);
    oDataParams.setSkip(currPage * pageSize);
    this.domainModel.getAllInternalUsers(oDataParams).subscribe({
      complete: () => {
        this._loadingOpts.removeRequest(lm);
        this._loadingInternalUsers.next(false);
      },
      error: (error: CustomError) => {
        this._loadingOpts.removeRequest(lm);
        this._loadingInternalUsers.next(false);
        this.toastService.publishError(error);
      }
    });
  }

  public refreshInternalUsers(): void {
    combineLatest([
      this.internalUserFilterRequest$,
      this.internalUserOrderbyRequest$,
      this.currPageNumber$,
      this.pageSize$
    ]).once(([filterReq, orderbyReq, currPage, pageSize]) => {
      this.fetchInternalUsers(filterReq, orderbyReq, currPage, pageSize, false);
    });
  }
}
